import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { getHistory, getLiked, getGeneratedImageUrl, deleteDesign, toggleLike, incrementView, setVisibility } from '../services/api';
import { useLang } from '../i18n/LanguageContext';
import { useAuth } from '../auth/AuthContext';

const TAB_MINE = 'mine';
const TAB_LIKED = 'liked';

export default function GalleryPage() {
  const { t } = useLang();
  const { user, isAdmin } = useAuth();
  const [tab, setTab] = useState(TAB_MINE);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  const loadData = useCallback(async (activeTab) => {
    setLoading(true);
    setError('');
    try {
      const { data } = activeTab === TAB_MINE ? await getHistory() : await getLiked();
      setDesigns(data);
    } catch (err) {
      setError(t.loadFailed);
    }
    setLoading(false);
  }, [t]);

  useEffect(() => { loadData(tab); }, [tab, loadData]);

  const switchTab = (newTab) => {
    if (newTab === tab) return;
    setSelected(null);
    setTab(newTab);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm(t.deleteConfirmOutfit)) return;
    try {
      await deleteDesign(id);
      setDesigns(prev => prev.filter(d => d.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (err) {
      setError(err.response?.data?.error || t.deleteFailed);
    }
  };

  const handleVisibility = async (e, id, currentVisibility) => {
    e.stopPropagation();
    const newVisibility = currentVisibility === 'Public' ? 'Private' : 'Public';
    try {
      await setVisibility(id, newVisibility);
      setDesigns(prev => prev.map(d => d.id === id ? { ...d, visibility: newVisibility } : d));
      if (selected?.id === id) setSelected(prev => ({ ...prev, visibility: newVisibility }));
    } catch (err) {
      setError(err.response?.data?.error || t.deleteFailed);
    }
  };

  const handleLike = async (e, id) => {
    e.stopPropagation();
    if (!user) return;
    try {
      const { data } = await toggleLike(id);
      if (tab === TAB_LIKED && !data.liked) {
        // Unliked from liked tab — remove it
        setDesigns(prev => prev.filter(d => d.id !== id));
        if (selected?.id === id) setSelected(null);
      } else {
        setDesigns(prev => prev.map(d =>
          d.id === id ? { ...d, likeCount: data.likeCount, isLikedByMe: data.liked } : d
        ));
        if (selected?.id === id) {
          setSelected(prev => ({ ...prev, likeCount: data.likeCount, isLikedByMe: data.liked }));
        }
      }
    } catch (err) { /* ignore */ }
  };

  const handleSelect = async (d) => {
    setSelected(d);
    try {
      const { data } = await incrementView(d.id);
      setDesigns(prev => prev.map(item =>
        item.id === d.id ? { ...item, viewCount: data.viewCount } : item
      ));
      setSelected(prev => prev ? { ...prev, viewCount: data.viewCount } : prev);
    } catch (err) { /* ignore */ }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) setSelected(null);
  };

  const selectedIdx = selected ? designs.findIndex(d => d.id === selected.id) : -1;
  const goNext = () => { if (selectedIdx < designs.length - 1) handleSelect(designs[selectedIdx + 1]); };
  const goPrev = () => { if (selectedIdx > 0) handleSelect(designs[selectedIdx - 1]); };

  useEffect(() => {
    const handleKey = (e) => {
      if (!selected) return;
      if (e.key === 'Escape') setSelected(null);
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  const isOwner = (d) => user && user.id === d.userId;
  const isPremiumPlus = user?.subscriptionStatus === 'Premium+';

  const handleDownload = (e, d) => {
    e.stopPropagation();
    window.open(getGeneratedImageUrl(d.imageFileName) + '?download=true', '_blank');
  };

  const tabs = [
    { key: TAB_MINE, icon: 'palette', label: t.galleryMyDesigns },
    { key: TAB_LIKED, icon: 'favorite', label: t.galleryLiked },
  ];

  return (
    <div className="max-w-[1100px] w-full mx-auto px-6 py-8 pb-12 space-y-8">
      
      {/* Segmented Tabs */}
      <div className="flex gap-2 bg-gray-50 p-1.5 rounded-xl border border-border w-fit shadow-sm">
        {tabs.map(({ key, icon, label }) => (
          <button
            key={key}
            onClick={() => switchTab(key)}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer ${
              tab === key
                ? 'bg-white text-[#FA6868] shadow-sm border border-gray-100'
                : 'text-[#64748b] hover:text-[#FA6868] hover:bg-gray-100/50'
            }`}
          >
            <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: key === TAB_LIKED && tab === key ? "'FILL' 1" : "'FILL' 0" }}>
              {icon}
            </span>
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-50 text-[#FA6868] rounded-lg text-sm border border-red-100">{error}</div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-surface rounded-xl p-16 shadow-soft border border-border text-center mt-4">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-[#FA6868] rounded-full mx-auto animate-spin"></div>
          <p className="text-[#64748b] mt-5 text-sm">{t.loadingImages}</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && designs.length === 0 && !error && (
        <div className="bg-surface rounded-xl p-16 shadow-soft border border-border text-center">
          <span className="material-symbols-outlined text-5xl text-gray-200 block mb-3">
            {tab === TAB_MINE ? 'palette' : 'favorite'}
          </span>
          <p className="text-[#64748b] text-base font-medium">
            {tab === TAB_MINE ? t.galleryNoDesigns : t.galleryNoLiked}
          </p>
        </div>
      )}

      {/* Grid */}
      {!loading && designs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {designs.map((d) => (
            <div
              key={d.id}
              className="group bg-surface rounded-xl overflow-hidden shadow-soft border border-border hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer"
            >
              <div
                className="w-full bg-gray-50 flex items-center justify-center p-3"
                style={{ height: 450 }}
                onClick={() => handleSelect(d)}
              >
                <img
                  src={getGeneratedImageUrl(d.imageFileName)}
                  alt={`Outfit #${d.id}`}
                  className="max-w-full max-h-full object-contain rounded-lg group-hover:scale-[1.02] transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-surface">
                <div className="flex items-center gap-3">
                  {/* Like */}
                  <button
                    className={`flex items-center gap-1 text-sm transition-colors duration-200 p-1.5 rounded-md cursor-pointer ${
                      d.isLikedByMe ? 'text-[#FA6868]' : 'text-[#64748b] hover:text-[#FA6868]'
                    } hover:bg-red-50`}
                    onClick={(e) => handleLike(e, d.id)}
                    title={d.isLikedByMe ? 'Unlike' : 'Like'}
                  >
                    <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: d.isLikedByMe ? "'FILL' 1" : "'FILL' 0" }}>
                      favorite
                    </span>
                    <span className="text-xs font-medium">{d.likeCount || 0}</span>
                  </button>
                  {/* Views */}
                  <span className="flex items-center gap-1 text-[#64748b] text-sm" title="Views">
                    <span className="material-symbols-outlined text-[18px]">visibility</span>
                    <span className="text-xs font-medium">{d.viewCount || 0}</span>
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {/* Visibility toggle - Premium+ only */}
                  {isOwner(d) && isPremiumPlus && (
                    <button
                      className={`transition-colors duration-200 text-base p-1.5 rounded-md cursor-pointer ${
                        d.visibility === 'Private' ? 'text-[#fbbf24] hover:text-amber-500 hover:bg-amber-50' : 'text-[#64748b] hover:text-[#FA6868] hover:bg-red-50'
                      }`}
                      onClick={(e) => handleVisibility(e, d.id, d.visibility)}
                      title={d.visibility === 'Private' ? t.visibilityPrivate : t.visibilityPublic}
                    >
                      <span className="material-symbols-outlined text-[18px]">
                        {d.visibility === 'Private' ? 'lock' : 'public'}
                      </span>
                    </button>
                  )}
                  <button
                    className="text-[#64748b] hover:text-[#FA6868] transition-colors duration-200 p-1.5 rounded-md hover:bg-red-50 cursor-pointer"
                    onClick={(e) => handleDownload(e, d)}
                    title="Download"
                  >
                    <span className="material-symbols-outlined text-[18px]">download</span>
                  </button>
                  {(isAdmin || (isOwner(d) && isPremiumPlus)) && (
                    <button
                      className="text-[#64748b] hover:text-red-500 transition-colors duration-200 text-base p-1.5 rounded-md hover:bg-red-50 cursor-pointer"
                      onClick={(e) => handleDelete(e, d.id)}
                      title="Delete"
                    >
                      <span className="material-symbols-outlined text-[18px]">delete</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {selected && ReactDOM.createPortal(
        <div
          className="fixed inset-0 modal-overlay backdrop-blur-sm flex items-center justify-center z-[9999]"
          onClick={handleOverlayClick}
          style={{ animation: 'fadeIn 0.2s ease' }}
        >
          {/* Close */}
          <button
            className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full modal-btn-icon flex items-center justify-center transition-colors cursor-pointer"
            onClick={() => setSelected(null)}
          >
            <span className="material-symbols-outlined text-[22px]">close</span>
          </button>

          {/* Counter */}
          <div className="absolute top-6 left-6 z-10 px-4 py-2 rounded-lg modal-counter text-xs font-medium">
            {selectedIdx + 1} / {designs.length}
          </div>

          {/* Prev arrow */}
          {selectedIdx > 0 && (
            <button
              className="absolute left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full modal-btn-icon flex items-center justify-center transition-colors cursor-pointer"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
            >
              <span className="material-symbols-outlined text-[26px]">chevron_left</span>
            </button>
          )}

          {/* Next arrow */}
          {selectedIdx < designs.length - 1 && (
            <button
              className="absolute right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full modal-btn-icon flex items-center justify-center transition-colors cursor-pointer"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
            >
              <span className="material-symbols-outlined text-[26px]">chevron_right</span>
            </button>
          )}

          {/* Image + bottom bar */}
          <div className="flex flex-col items-center max-w-[90vw] max-h-[95vh] modal-card-shadow" onClick={(e) => e.stopPropagation()}>
            <img
              src={getGeneratedImageUrl(selected.imageFileName)}
              alt={`Outfit #${selected.id}`}
              className="block max-w-full max-h-[85vh] rounded-t-xl object-contain modal-img-bg"
            />
            {/* Stats bar */}
            <div className="w-full flex items-center justify-between px-6 py-4 modal-stats-bar rounded-b-xl">
              <div className="flex items-center gap-6">
                <button
                  className={`flex items-center gap-2 text-sm transition-colors duration-200 px-3 py-1.5 rounded-lg cursor-pointer ${
                    selected.isLikedByMe ? 'text-[#FA6868] bg-[#FA6868]/10' : 'modal-stats-text hover:text-[#FA6868] hover:bg-white/10'
                  }`}
                  onClick={(e) => handleLike(e, selected.id)}
                >
                  <span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: selected.isLikedByMe ? "'FILL' 1" : "'FILL' 0" }}>
                    favorite
                  </span>
                  <span className="font-semibold">{selected.likeCount || 0}</span>
                </button>
                <span className="flex items-center gap-2 modal-stats-text text-sm px-3 py-1.5">
                  <span className="material-symbols-outlined text-[20px]">visibility</span>
                  <span className="font-semibold">{selected.viewCount || 0}</span>
                </span>
              </div>
              <span className="modal-stats-date text-xs font-medium">{new Date(selected.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}